"use client";

import { useSendContactMessage } from "@/hooks/main-hooks";
import { getData } from "@/request/api";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowUpRight,
  CheckCircle2,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Send,
} from "lucide-react";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useState } from "react";
import { useForm } from "react-hook-form";

type ContactForm = {
  name: string;
  email: string;
  phone?: string;
  message: string;
};

export default function Contact() {
  const locale = useLocale();
  const isAr = locale === "ar";
  const [sent, setSent] = useState(false);

  const { data: info } = useQuery({
    queryKey: ["contact-info"],
    queryFn: async () => {
      const res = await getData("contact-info");
      return res.data?.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const { mutateAsync: sendMessage, isPending } = useSendContactMessage();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactForm>();

  const onSubmit = async (data: ContactForm) => {
    try {
      await sendMessage({
        name: data.name,
        email: data.email,
        phone: data.phone,
        message: data.message,
      });
      setSent(true);
      reset();
    } catch {
      // Error toast handled inside the mutation hook.
    }
  };

  const contactItems = [
    {
      icon: Phone,
      label: isAr ? "الهاتف" : "Phone",
      value: info?.phone || "+20 100 000 0000",
      href: info?.phone ? `tel:${info.phone}` : undefined,
    },
    {
      icon: Mail,
      label: isAr ? "البريد الإلكتروني" : "Email",
      value: info?.email || "info@aldawlia-eg.com",
      href: info?.email ? `mailto:${info.email}` : undefined,
    },
    {
      icon: MapPin,
      label: isAr ? "العنوان" : "Address",
      value: isAr
        ? info?.addressAr || "طريق المنطقة الصناعية - القاهرة - مصر"
        : info?.addressEn || "Industrial Zone – Cairo – Egypt",
      href: info?.mapsUrl || undefined,
    },
  ];

  return (
    <div
      className="min-h-screen flex flex-col lg:flex-row"
      dir={isAr ? "rtl" : "ltr"}
    >
      {/* ─── LEFT PANEL — image + brand + contact info ─── */}
      <div className="relative lg:sticky lg:top-0 lg:h-screen lg:w-[42%] overflow-hidden shrink-0">
        {/* Factory Image */}
        <Image
          src="/facilities1.jpeg"
          alt={isAr ? "مصنع الدولية" : "Al Dawlia Factory"}
          fill
          className="object-cover object-center"
          priority
          sizes="42vw"
        />

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-linear-to-t from-brand-navy via-brand-navy/80 to-brand-navy/30" />
        <div className="absolute inset-0 bg-brand-navy/20" />

        {/* Decorative diagonal stripe */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 0, transparent 50%)",
            backgroundSize: "24px 24px",
          }}
        />

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-between px-10 py-16 lg:py-24">
          {/* Top — badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 text-xs font-bold tracking-widest text-white/80 uppercase">
              <span className="w-2 h-2 rounded-full bg-brand-red animate-pulse" />
              {isAr ? "الدولية " : "Al Dawlia Aluminum"}
            </span>
          </motion.div>

          {/* Mid — headline */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="space-y-6"
          >
            <h1
              className="text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight"
              style={{ textShadow: "0 2px 30px rgba(0,0,0,0.5)" }}
            >
              {isAr ? (
                <>
                  صناعة
                  <br />
                  <span className="text-brand-red">ألومنيوم</span>
                  <br />
                  يدوم طويلاً
                </>
              ) : (
                <>
                  We craft
                  <br />
                  <span className="text-brand-red">aluminum</span>
                  <br />
                  that lasts
                </>
              )}
            </h1>

            <p className="text-white/60 text-base leading-relaxed max-w-xs">
              {isAr
                ? "فريقنا الهندسي جاهز للرد على استفساراتك وتقديم أفضل الحلول."
                : "Our engineering team is ready to answer your inquiries and deliver the best solutions."}
            </p>

            {/* Divider */}
            <div className="flex items-center gap-4">
              <div className="h-px flex-1 bg-white/20" />
              <div className="w-1.5 h-1.5 rounded-full bg-brand-red" />
              <div className="h-px flex-1 bg-white/20" />
            </div>

            {/* Contact info items */}
            <div className="space-y-4">
              {contactItems.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: isAr ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                >
                  {item.href ? (
                    <a
                      href={item.href}
                      target={
                        item.href.startsWith("http") ? "_blank" : undefined
                      }
                      rel={
                        item.href.startsWith("http")
                          ? "noopener noreferrer"
                          : undefined
                      }
                      className="group flex items-start gap-4 hover:bg-white/5 rounded-xl p-3 -mx-3 transition-all"
                    >
                      <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0 group-hover:bg-brand-red/80 transition-colors">
                        <item.icon size={16} className="text-white" />
                      </div>
                      <div>
                        <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase mb-0.5">
                          {item.label}
                        </p>
                        <p
                          dir="ltr"
                          className="text-white font-semibold text-sm"
                        >
                          {item.value}
                        </p>
                      </div>
                      {item.href.startsWith("http") && (
                        <ArrowUpRight
                          size={14}
                          className="text-white/30 group-hover:text-white mt-1 ms-auto shrink-0 transition-colors"
                        />
                      )}
                    </a>
                  ) : (
                    <div className="flex items-start gap-4 p-3 -mx-3">
                      <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                        <item.icon size={16} className="text-white" />
                      </div>
                      <div>
                        <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase mb-0.5">
                          {item.label}
                        </p>
                        <p className="text-white font-semibold text-sm">
                          {item.value}
                        </p>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Bottom — tagline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="text-white/25 text-xs tracking-widest uppercase"
          >
            {isAr
              ? "منذ عقود من التميز الصناعي"
              : "Decades of industrial excellence"}
          </motion.p>
        </div>
      </div>

      {/* ─── RIGHT PANEL — form ─── */}
      <div className="flex-1 bg-[#f7f6f2] flex items-start lg:items-center justify-center px-6 py-20 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="w-full max-w-xl"
        >
          {/* Heading */}
          <div className="mb-12">
            <p className="text-xs font-black tracking-[0.35em] uppercase text-brand-red mb-4">
              {isAr ? "راسلنا الآن" : "Send us a message"}
            </p>
            <h2 className="text-4xl lg:text-5xl font-black text-brand-navy tracking-tight leading-tight whitespace-pre-line">
              {isAr ? "كيف نقدر\nنساعدك؟" : "How can we\nhelp you?"}
            </h2>
          </div>

          <AnimatePresence mode="wait">
            {sent ? (
              /* ── Success State ── */
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                className="bg-white rounded-3xl p-12 shadow-xl border border-brand-navy/5 text-center space-y-6"
              >
                <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto">
                  <CheckCircle2 size={40} className="text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-black text-brand-navy mb-2">
                    {isAr ? "تم الإرسال بنجاح!" : "Message Sent!"}
                  </p>
                  <p className="text-brand-navy/50 text-sm leading-relaxed">
                    {isAr
                      ? "شكراً لتواصلك معنا. سيرد عليك فريقنا خلال 24 ساعة."
                      : "Thank you for reaching out. Our team will reply within 24 hours."}
                  </p>
                </div>
                <button
                  onClick={() => setSent(false)}
                  className="text-sm font-bold text-brand-blue hover:underline"
                >
                  {isAr ? "إرسال رسالة أخرى" : "Send another message"}
                </button>
              </motion.div>
            ) : (
              /* ── Form ── */
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onSubmit={handleSubmit(onSubmit)}
                className="bg-white rounded-3xl p-8 lg:p-10 shadow-xl border border-brand-navy/5 space-y-6"
              >
                {/* Name */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold tracking-widest uppercase text-brand-navy/40">
                    {isAr ? "الاسم الكامل" : "Full Name"}
                    <span className="text-brand-red ms-1">*</span>
                  </label>
                  <input
                    {...register("name", { required: true })}
                    placeholder={isAr ? "محمد أحمد" : "John Smith"}
                    className={`w-full bg-brand-creamy/60 rounded-xl px-5 py-4 text-brand-navy font-medium outline-none border-2 transition-all placeholder:text-brand-navy/25 ${
                      errors.name
                        ? "border-red-300 focus:border-red-400"
                        : "border-transparent focus:border-brand-blue focus:bg-white"
                    }`}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500">
                      {isAr ? "الاسم مطلوب" : "Name is required"}
                    </p>
                  )}
                </div>

                {/* Email + Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold tracking-widest uppercase text-brand-navy/40">
                      {isAr ? "البريد الإلكتروني" : "Email"}
                      <span className="text-brand-red ms-1">*</span>
                    </label>
                    <input
                      {...register("email", {
                        required: true,
                        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      })}
                      type="email"
                      dir="ltr"
                      placeholder="name@company.com"
                      className={`w-full bg-brand-creamy/60 rounded-xl px-5 py-4 text-brand-navy font-medium outline-none border-2 transition-all placeholder:text-brand-navy/25 ${
                        errors.email
                          ? "border-red-300 focus:border-red-400"
                          : "border-transparent focus:border-brand-blue focus:bg-white"
                      }`}
                    />
                    {errors.email && (
                      <p className="text-xs text-red-500">
                        {isAr ? "بريد إلكتروني غير صالح" : "Invalid email"}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold tracking-widest uppercase text-brand-navy/40">
                      {isAr ? "الهاتف" : "Phone"}
                      <span className="text-brand-navy/20 ms-1 text-[10px] normal-case tracking-normal">
                        ({isAr ? "اختياري" : "optional"})
                      </span>
                    </label>
                    <input
                      {...register("phone")}
                      type="tel"
                      dir="ltr"
                      placeholder="+20 1xx xxx xxxx"
                      className="w-full bg-brand-creamy/60 rounded-xl px-5 py-4 text-brand-navy font-medium outline-none border-2 border-transparent focus:border-brand-blue focus:bg-white transition-all placeholder:text-brand-navy/25"
                    />
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold tracking-widest uppercase text-brand-navy/40">
                    {isAr
                      ? "تفاصيل مشروعك أو استفسارك"
                      : "Project Details / Message"}
                    <span className="text-brand-red ms-1">*</span>
                  </label>
                  <textarea
                    {...register("message", { required: true })}
                    rows={5}
                    placeholder={
                      isAr
                        ? "اشرح مشروعك أو استفسارك بالتفصيل..."
                        : "Describe your project or inquiry in detail..."
                    }
                    className={`w-full bg-brand-creamy/60 rounded-xl px-5 py-4 text-brand-navy font-medium outline-none border-2 transition-all resize-none placeholder:text-brand-navy/25 ${
                      errors.message
                        ? "border-red-300 focus:border-red-400"
                        : "border-transparent focus:border-brand-blue focus:bg-white"
                    }`}
                  />
                  {errors.message && (
                    <p className="text-xs text-red-500">
                      {isAr ? "الرسالة مطلوبة" : "Message is required"}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isPending}
                  className="group w-full bg-brand-navy text-white font-black tracking-[0.15em] uppercase text-sm py-5 rounded-2xl hover:bg-brand-red transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isPending ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      {isAr ? "جارٍ الإرسال..." : "Sending..."}
                    </>
                  ) : (
                    <>
                      {isAr ? "إرسال الرسالة" : "Send Message"}
                      <Send
                        size={16}
                        className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform rtl:rotate-180"
                      />
                    </>
                  )}
                </button>

                <p className="text-center text-[11px] text-brand-navy/30 leading-relaxed">
                  {isAr
                    ? "سيتواصل معك فريقنا خلال 24 ساعة من استلام رسالتك."
                    : "Our team will contact you within 24 hours of receiving your message."}
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
